import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useState,
} from 'react';
import classNames from 'classnames';
import {
  ModalProps,
  ModalRef,
} from 'ncoded-component-library/build/components/organisms/Modal/Modal.component';
import useCallbackRef from 'hooks/useCallbackRef';
import { Loader, Modal } from 'ncoded-component-library';
import { useTranslation } from 'react-i18next';
import UserWithAccess from './components/UserWithAccess';
import useCallbackAsync from 'hooks/useCallbackAsync';
import dataRoomApi from 'router/subrouters/DataRoom/dataRoomApi';
import { DataRoomMembersWithAccess } from 'router/subrouters/DataRoom/models/DataRoomNode';
import { DataRoomMember } from 'router/subrouters/DataRoom/models/DataRoomMember';
import { DataRoomShareableLinkConsumer } from 'models/ShareableLink';

import './UsersWithAccessModal.styles.scss';

const removeMemberWithId = (
  members: (DataRoomMember | DataRoomShareableLinkConsumer)[],
  memberId: string,
) => members.filter((member) => member._id !== memberId);

const removeAccessForMember = (
  members: (DataRoomMember | DataRoomShareableLinkConsumer)[],
  memberId: string,
) =>
  members.map((member) =>
    member._id !== memberId ? member : { ...member, canRevoke: false },
  );

type UsersWithAccessModalProps = {
  className?: string;
  nodeId: string;
  dataRoomId: string;
  decrementNumberOfMemberAccess: () => void;
} & ModalProps;

const UsersWithAccessModal: React.ForwardRefRenderFunction<
  ModalRef,
  UsersWithAccessModalProps
> = (props, ref) => {
  const {
    className,
    nodeId,
    dataRoomId,
    decrementNumberOfMemberAccess,
    ...rest
  } = props;

  const [membersWithAccess, setMembersWithAccess] =
    useState<DataRoomMembersWithAccess>();

  const {
    sellSideMembers = [],
    buySideMembers = [],
    shareableLinkConsumers = [],
  } = membersWithAccess || {};

  const [modal, modalRef] = useCallbackRef<ModalRef>();

  useImperativeHandle(ref, () => modal, [modal]);

  const classes = classNames('users-with-access-modal', className);

  const { t } = useTranslation();

  const [getMembers, isLoading] = useCallbackAsync(async () => {
    const { data } = await dataRoomApi.nodes.getMembersWithAccess(nodeId);
    setMembersWithAccess(data);
  });

  const removeMember = useCallback(
    (
      memberId: string,
      hasAccessToOwnFiles: boolean,
      isDataRoomMember: boolean,
    ) => {
      if (!hasAccessToOwnFiles) decrementNumberOfMemberAccess();

      const action = hasAccessToOwnFiles
        ? removeAccessForMember
        : removeMemberWithId;

      setMembersWithAccess((prev) =>
        isDataRoomMember
          ? {
              ...prev,
              sellSideMembers: action(
                prev.sellSideMembers,
                memberId,
              ) as DataRoomMember[],
              buySideMembers: action(
                prev.buySideMembers,
                memberId,
              ) as DataRoomMember[],
            }
          : {
              ...prev,
              shareableLinkConsumers: action(
                prev.shareableLinkConsumers,
                memberId,
              ) as DataRoomShareableLinkConsumer[],
            },
      );
    },
    [decrementNumberOfMemberAccess],
  );

  const renderSection = useCallback(
    (
      title: string,
      members: Partial<DataRoomMember>[],
      isDataRoomMember = true,
    ) => {
      if (!members.length) return null;

      return (
        <section>
          <p className="section-title">{title}</p>
          {members.map((member) => (
            <UserWithAccess
              key={member._id}
              member={member}
              nodeId={nodeId}
              dataRoomId={dataRoomId}
              isDataRoomMember={isDataRoomMember}
              onRevokeAccessSuccess={() =>
                removeMember(
                  member._id,
                  member.hasAccessToOwnFiles,
                  isDataRoomMember,
                )
              }
            />
          ))}
        </section>
      );
    },
    [nodeId, dataRoomId, removeMember],
  );

  useEffect(() => {
    if (modal?.openState) {
      getMembers();
    }
  }, [getMembers, modal?.openState]);

  return (
    <Modal
      ref={modalRef}
      className={classes}
      title={t('membersWithAccess')}
      {...rest}
    >
      {isLoading && <Loader />}

      {renderSection(t('secureLinksUsers'), shareableLinkConsumers, false)}
      {renderSection(t('buySideUsers'), buySideMembers)}
      {renderSection(t('sellSideUsers'), sellSideMembers)}
    </Modal>
  );
};

export default forwardRef(UsersWithAccessModal);
